// ================= THE RULES PAGE =================
//
// THE CALLER MechanicsDictionary.all() WAS WRITTEN FOR. That function has carried the comment
// "a caller building a glossary index gets one row for them rather than three identical ones"
// since it was written, and until this file it had no caller at all outside its own test. The
// same is true one step down: js/mechanics-coach.js says of `reset()` that "there is deliberately
// no UI for this yet ... a 'reset tutorials' button is a settings-screen decision rather than a
// mechanics one". This page is that decision, made.
//
// THREE SOURCES, AND THE SPLIT BETWEEN THEM IS THE WHOLE DESIGN.
//
//   js/mechanics-dictionary.js  every Attack Type, follow-through, Defense Type and range verdict
//   js/style-passives.js        the four Foundation passives
//   js/rules-content.js         the systems no card carries, so no module can render them
//
// The first two are RENDERED, never restated -- and rendered through their OWN renderers,
// `sheetBodyHtml` in each case, which is the same function the in-bout glossary sheet and the
// passive sheet call. That is not tidiness, it is the anti-drift mechanism: if this page built its
// own markup from `entry.long`, the day somebody adds a field to the sheet, the reference page
// silently stops showing it. One renderer, two callers, no way to disagree.
//
// The brief the whole mechanics-visibility effort runs on says it outright, and names this
// component last: "Do not reproduce the definitions separately in card, tutorial, resolution,
// library, and glossary components." tests/rules-content-test.js §3 enforces it as an assertion.
//
// WHAT IS DERIVED RATHER THAN WRITTEN. Under each glossary entry sits a line saying how many cards
// carry it and three examples. Those are computed at render by running the dictionary's own
// resolvers over window.CARDS, so a card retyped in the data changes this page without anybody
// editing it -- and a number nobody maintains is a number nobody can leave stale.
//
// NO GAME SHELL. This page loads no ui.js, no engine, no card tile and no sheet: it renders text
// and one button. The rule library-ui-test holds for the store's card sheet holds here too -- no
// control on this page may name `window.UI`, because there is no such object and a control that
// names one throws under a finger.
window.RulesPage = (function () {
  'use strict';

  const MD = () => window.MechanicsDictionary;
  const SP = () => window.StylePassives;
  const SB = () => window.StyleBriefs;
  const RC = () => window.RulesContent;
  const COACH = () => window.MechanicsCoach;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }

  function app() { return document.getElementById('app'); }

  // Announced rather than merely repainted. The reset changes a count and nothing else visible, so
  // without this a screen reader is told nothing happened.
  function announce(msg) {
    const box = document.getElementById('rules-status');
    if (box) box.textContent = msg;
  }

  // ---------------- which cards carry a mechanic ----------------
  //
  // ASKED, NOT TABULATED. The dictionary already owns "what type is this card" in three resolver
  // functions the bout uses, and those handle the awkward cases -- the 22 cards whose Attack Type
  // is recovered from the engine's finer category rather than from `v4.family`, for instance. A
  // count computed any other way here would be a second opinion about the same question, and the
  // one on this page would be the one nobody notices going wrong.
  //
  // rangeFit entries get nothing: a fit is a verdict about an exchange, not a property of a card,
  // so "on N cards" would be a category error rather than a missing number.
  const RESOLVER = {
    attackType: (M, card) => M.attackTypeOf(card),
    followThrough: (M, card) => M.followThroughOf(card),
    defenseType: (M, card) => M.defenseTypeOf(card),
    rangeFit: () => null
  };

  function carriers(kind, entry) {
    const M = MD(), CARDS = window.CARDS;
    const fn = RESOLVER[kind];
    if (!M || !CARDS || !fn) return { count: 0, names: [] };
    const names = [];
    let count = 0;
    Object.keys(CARDS).forEach(id => {
      const card = CARDS[id];
      const hit = fn(M, card);
      if (hit && hit.key === entry.key) {
        count++;
        if (names.length < 3 && card.name) names.push(card.name);
      }
    });
    return { count: count, names: names };
  }

  function carrierLine(kind, entry) {
    const c = carriers(kind, entry);
    if (!c.count) return '';
    const eg = c.names.length ? ' — for example ' + c.names.map(esc).join(', ') : '';
    return `<p class="rules-carriers">On ${c.count} card${c.count === 1 ? '' : 's'}${eg}.</p>`;
  }

  // ---------------- the bands ----------------

  function tocHtml() {
    const rc = RC(), M = MD(), sp = SP();
    const groups = [];
    if (rc) {
      groups.push({
        head: 'The game',
        links: rc.all().map(s => ({ id: s.id, label: s.title }))
      });
    }
    if (M && rc) {
      groups.push({
        head: 'The glossary',
        links: M.KINDS.map(k => ({ id: 'kind-' + k, label: (rc.KIND_HEADINGS[k] || {}).title || k }))
      });
    }
    if (SB()) groups.push({ head: 'Styles', links: [{ id: 'styles', label: 'The eight Styles' }] });
    if (sp) groups.push({ head: 'Passives', links: [{ id: 'passives', label: 'Foundation passives' }] });
    if (COACH()) groups.push({ head: 'Coaching', links: [{ id: 'coaching', label: 'The in-fight coaching' }] });
    return `
      <nav class="rules-toc" aria-label="On this page">
        ${groups.map(g => `
          <div class="rules-toc-group">
            <h2>${esc(g.head)}</h2>
            <ul>${g.links.map(l =>
              `<li><a href="#${esc(l.id)}">${esc(l.label)}</a></li>`).join('')}</ul>
          </div>`).join('')}
      </nav>`;
  }

  function sectionsHtml() {
    const rc = RC();
    if (!rc) return '';
    return `<div class="rules-sections">` + rc.all().map(s => `
      <section class="rules-section" id="${esc(s.id)}">
        <h2>${esc(s.title)}</h2>
        <p>${esc(s.body)}</p>
        ${(s.points && s.points.length)
          ? `<ul class="rules-points">${s.points.map(p => `<li>${esc(p)}</li>`).join('')}</ul>` : ''}
      </section>`).join('') + `</div>`;
  }

  // THE GLOSSARY, THROUGH THE DICTIONARY'S OWN RENDERER.
  //
  // `all()` is deduplicated, which matters exactly once and visibly: the three Authored
  // follow-through keys share one entry object, so a page that walked the tables itself would
  // print that definition three times under three different names. That dedupe is the documented
  // reason the function exists, and this is the caller it was written for.
  //
  // The entries are grouped by KIND for reading, so the walk is per-kind and `all()` is used as the
  // set that must be covered rather than as the iteration order -- with a check that the two agree,
  // because a kind added to the dictionary and not to KIND_HEADINGS would otherwise vanish from
  // this page in silence.
  function glossaryHtml() {
    const M = MD(), rc = RC();
    if (!M || !rc) return '';
    const seen = [];
    const kinds = M.KINDS.map(kind => {
      const heading = rc.KIND_HEADINGS[kind] || { title: kind, blurb: '' };
      const entries = [];
      Object.keys(M.TABLES[kind]).forEach(key => {
        const entry = M.TABLES[kind][key];
        if (entry && entries.indexOf(entry) < 0 && seen.indexOf(entry) < 0) {
          entries.push(entry);
          seen.push(entry);
        }
      });
      return `
        <section class="rules-kind" id="kind-${esc(kind)}">
          <h2>${esc(heading.title)}</h2>
          <p class="rules-kind-blurb">${esc(heading.blurb)}</p>
          ${entries.map(entry => `
            <article class="rules-entry" id="${esc(entry.key.toLowerCase())}">
              <h3>${M.badgeHtml(entry)}</h3>
              <div class="rules-entry-body">${M.sheetBodyHtml(entry)}</div>
              ${carrierLine(kind, entry)}
            </article>`).join('')}
        </section>`;
    }).join('');
    return `<div class="rules-glossary">${kinds}</div>`;
  }

  // THE PASSIVES, THROUGH THEIRS. Same argument as the glossary: js/style-passives.js has a
  // `sheetBodyHtml` written for the in-bout sheet, and this page calls it rather than
  // reformatting `long` and `sample` into a second shape.
  // ---------------- the eight Styles ----------------
  //
  // Phase H of the after-the-bell plan. This page covered the exchange, range, Intent, Speed,
  // Power and Guard, Openings, Stamina, Fury-Flow, Off-Balance, winning, and how to build a deck --
  // and said nothing whatsoever about the eight Styles a player picks between before any of that
  // applies. The Foundation passives section below was the only Style content on the page, and it
  // describes four families rather than eight identities.
  //
  // PAIRED WITH THE PASSIVE, not merely listed. Each entry names the Foundation it shares and links
  // to that passive's own entry further down, so the fact that Shotokan and Taekwondo run the same
  // free rule -- the thing that most surprises a player comparing them -- is visible rather than
  // inferred from two separate readings.
  function stylesHtml() {
    const sb = SB();
    if (!sb) return '';
    const entries = sb.all();
    if (!entries.length) return '';
    const sp = SP();
    return `
      <section class="rules-styles" id="styles">
        <h2>The eight Styles</h2>
        <p class="rules-kind-blurb">Your Style decides twelve of your thirty cards outright and the
          Foundation that supplies eight more, so it is the first and largest decision in a deck.
          The eight pair into four Foundations, and the two Styles sharing one run the same
          always-on rule from very different card pools.</p>
        ${entries.map(b => {
          const passive = (sp && sp.forFighter) ? sp.forFighter(b.style) : null;
          return `
          <article class="rules-entry" id="style-${esc(b.style)}">
            <h3>${esc(b.label)}</h3>
            <p class="rs-meta"><span class="rs-key">Range</span> ${esc(b.range)}
              ${b.foundationLabel ? `<span class="rs-sep">&middot;</span>
                <span class="rs-key">Foundation</span>
                ${passive
                  ? `<a href="#passive-${esc(passive.key)}">${esc(b.foundationLabel)}</a>`
                  : esc(b.foundationLabel)}` : ''}</p>
            <p>${esc(b.gameplan)}</p>
            <p class="rs-feel">${esc(b.feel)}</p>
          </article>`;
        }).join('')}
      </section>`;
  }

  function passivesHtml() {
    const sp = SP();
    if (!sp) return '';
    return `
      <section class="rules-passives" id="passives">
        <h2>Foundation passives</h2>
        <p class="rules-kind-blurb">Every deck's Style belongs to one of four families, and the
          family brings a rule that is always on. You do not choose it and you cannot turn it
          off — it is what the family is for.</p>
        ${sp.all().map(entry => `
          <article class="rules-entry" id="passive-${esc(entry.key)}">
            <h3>${esc(entry.name)}</h3>
            <!-- NO TIMING OR FAMILY CHIP BESIDE THE NAME. The first build put both there and they
                 were already on screen: StylePassives' own sheetBodyHtml opens with an
                 sp-sheet-kicker line reading "Fulcrum &amp; Flow · Foundation passive · End of
                 exchange", so the heading was restating the sentence directly beneath it. Same
                 correction, same reason, as the library sheet's duplicated gloss: when a renderer
                 already says something, the surface around it does not say it again. -->
            <div class="rules-entry-body">${sp.sheetBodyHtml(entry)}</div>
          </article>`).join('')}
      </section>`;
  }

  // ---------------- the coaching corner ----------------
  //
  // WHAT THIS PAGE OWES THE COACH. The first-trigger coach teaches each mechanic once, on the
  // exchange where it first fires, and then never again -- which is the right design and leaves a
  // player with no way to see it twice. `reset()` has existed the whole time with a comment saying
  // its UI belongs on a settings screen that did not exist. It does now.
  //
  // NO CONFIRMATION DIALOG. The action is safe (it teaches more, never less), reversible by
  // playing, and its effect is stated before it is taken and again after. A modal here would be
  // ceremony around a button whose worst outcome is reading one sentence you already knew.
  //
  // SCOPED TO THE COACH AND NOTHING ELSE. `reset()` clears the `sc.coach.` prefix only -- deck
  // drafts, the Home explainer's state and the credits queue all live in the same localStorage and
  // are none of this page's business. The page never touches a key itself; it asks through
  // `taught()` and `reset()`, so the per-mechanic, per-ruleset key scheme stays in the one file
  // that documents it.
  function coachCount() {
    const M = MD(), coach = COACH();
    if (!M || !coach || !coach.taught) return null;
    const entries = M.all();
    return { taught: entries.filter(e => coach.taught(e.key)).length, total: entries.length };
  }

  // ---------------- the readout nobody is shown ----------------
  //
  // Phase I of the after-the-bell plan. js/mechanics-telemetry.js counts every coach line shown,
  // every sheet opened and every counterfactual rendered, and its own header explains that it is
  // "deliberately not a UI: the brief asks for instrumentation to evaluate the redesign, not for a
  // dashboard inside a game". That is still true for players -- and it left the data with no reader
  // at all, on the one question the 2026-09-05 persona review could not answer from the code: is
  // any of the teaching actually landing.
  //
  // BEHIND ?telemetry, LINKED FROM NOWHERE. The module's promise to the player is kept exactly --
  // there is no dashboard in the game and no control that reveals one -- while a developer with a
  // URL can read what this device recorded. It is the same posture as the seed in the bout log:
  // present for whoever needs it, invisible to whoever does not.
  //
  // DEVICE-LOCAL AND SAYING SO. This is one browser's localStorage, not a population. A number here
  // is a debugging aid, and the line under the table exists so nobody quotes it as a finding.
  function telemetryHtml() {
    const T = window.MechanicsTelemetry;
    if (!T || !T.snapshot) return '';
    try {
      if (String(location.search || '').indexOf('telemetry') < 0) return '';
    } catch (e) { return ''; }
    const data = T.snapshot();
    const keys = Object.keys(data).filter(k => k.charAt(0) !== '_')
      .sort((a, b) => data[b] - data[a]);
    return `
      <section class="rules-telemetry" id="telemetry">
        <h2>What this device has been shown</h2>
        <p class="rules-kind-blurb">Counted locally by the game as it teaches. This is one browser's
          own record — it is a debugging aid, not a measurement of anything but this device.</p>
        <p class="rules-telemetry-summary">${esc(T.summary())}</p>
        ${keys.length ? `<table class="rules-telemetry-table">
          <thead><tr><th scope="col">Interaction</th><th scope="col">Count</th></tr></thead>
          <tbody>${keys.map(k =>
            `<tr><td>${esc(k)}</td><td>${esc(String(data[k]))}</td></tr>`).join('')}</tbody>
        </table>` : ''}
      </section>`;
  }

  function coachHtml() {
    const coach = COACH();
    if (!coach || !coach.reset) return '';
    const c = coachCount();
    return `
      <section class="rules-coaching" id="coaching">
        <h2>The in-fight coaching</h2>
        <p>The game explains a mechanic the first time it actually happens to you, attached to the
          exchange that just happened, and then leaves you alone about it. Each one is remembered
          separately and per rules version, so a rule that changes is explained again.</p>
        <p class="rules-coach-count" id="rules-coach-count">${c
          ? `It has introduced ${c.taught} of ${c.total} mechanics on this device.`
          : ''}</p>
        <button type="button" class="rules-coach-reset" id="rules-coach-reset"
                onclick="RulesPage.resetCoaching()">Meet the coaching again</button>
        <p class="rules-coach-note">Nothing else is cleared — not your decks, not your credits, not
          your collection.</p>
      </section>`;
  }

  // Repaints the count in place rather than re-rendering the page: a full render would move focus
  // off the button that was just pressed, and there is nothing else on screen that changed.
  function resetCoaching() {
    const coach = COACH();
    if (!coach || !coach.reset) return false;
    coach.reset();
    const c = coachCount();
    const box = document.getElementById('rules-coach-count');
    if (box && c) box.textContent = 'It has introduced ' + c.taught + ' of ' + c.total +
      ' mechanics on this device.';
    announce('Coaching reset — the game will explain each mechanic again the first time it fires.');
    return true;
  }

  // ---------------- the screen ----------------

  function render() {
    const box = app();
    if (!box) return false;
    box.innerHTML = tocHtml() + sectionsHtml() + glossaryHtml() + stylesHtml() + passivesHtml() +
      coachHtml() + telemetryHtml();
    return true;
  }

  // THE CREDITS NUMBER, AND NOTHING ELSE FROM THE NETWORK. Same posture as library.html: the page
  // is complete and correct before this resolves, and if it never resolves the strip simply stays
  // hidden. A `file://` copy renders every word of this page.
  function loadStanding() {
    if (!window.StoreClient || !window.PageNav) return;
    Promise.resolve()
      .then(() => window.StoreClient.state())
      .then(s => { window.PageNav.render(s); })
      .catch(() => { /* no backend, no strip -- both are fine */ });
  }

  function boot() {
    render();
    loadStanding();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  return { render, resetCoaching, coachCount, carriers };
})();
